import { AccessDenied, AppShell, ErrorRetry, Spinner } from '@brush/ui';
import { useState } from 'react';
import { About } from './components/About';
import { SessionActions } from './components/SessionActions';
import { PointsSystemsArea } from './components/points/PointsSystemsArea';
import { OverviewArea } from './components/areas/OverviewArea';
import { RoomsArea } from './components/areas/RoomsArea';
import { SettingsArea } from './components/areas/SettingsArea';
import { TagsArea } from './components/areas/TagsArea';
import { TemplatesArea } from './components/areas/TemplatesArea';
import { ADMIN_VIEW, ADMIN_VIEWS, type AdminView } from './nav';
import { useCurrentUser } from './queries';
import { useScreenViewed, useSession, useSessionStarted } from './api';

const centered = 'grid min-h-screen place-items-center bg-ground p-6';

export function App() {
  const me = useCurrentUser();
  const { displayName, signOut } = useSession();
  // Once the app is in use: a visitor refused at the access check was not a console session.
  useSessionStarted(me.data?.isAdmin === true);
  const [view, setView] = useState<AdminView>('overview');
  useScreenViewed(me.data?.isAdmin === true ? view : null);

  // Access is decided before anything else renders: this app is for platform administrators.
  if (me.isLoading) {
    return (
      <div className={centered}>
        <Spinner label="Checking access…" />
      </div>
    );
  }
  if (me.isError || !me.data) {
    return (
      <div className={centered}>
        <ErrorRetry message="Couldn't verify your access." onRetry={() => void me.refetch()} />
      </div>
    );
  }
  if (!me.data.isAdmin) {
    return (
      <AccessDenied
        appName="Brush Admin"
        message={
          me.data.isRoomOperator
            ? 'This console is for platform administrators. Your account operates rooms, so use the Room app instead.'
            : 'This console is for platform administrators. Ask a platform administrator if you need access.'
        }
        accountName={me.data.name ?? displayName}
        onSignOut={signOut}
      />
    );
  }

  const area = ADMIN_VIEW[view];

  return (
    <AppShell
      appName="Brush"
      role="Platform Admin"
      title={area.label}
      description={area.description}
      nav={{ items: ADMIN_VIEWS, current: view, onNavigate: setView }}
      actions={<SessionActions />}
      footer={<About />}
    >
      {view === 'overview' ? (
        <OverviewArea />
      ) : view === 'rooms' ? (
        <RoomsArea />
      ) : view === 'tags' ? (
        <TagsArea />
      ) : view === 'templates' ? (
        <TemplatesArea />
      ) : view === 'points' ? (
        <PointsSystemsArea />
      ) : (
        <SettingsArea />
      )}
    </AppShell>
  );
}
